import { useState, useMemo, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { X } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { Calendar } from '@shared/components/ui/calendar';
import { BookingPageLayout } from '@features/booking/layouts';
import { useBookingFlow } from '@features/booking/flow';
import { useBooking } from '@features/booking/state';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { useLanguage } from '@core/i18n';
import { useAuth } from '@core/auth/AuthProvider';
import { useCalendarAvailability } from '@features/booking/hooks/useCalendarAvailability';
import { isDisplayTypeClickable } from '@features/booking/utils/availability';
import { fetchWaitingListAlternatives, fetchWaitingListGuaranteeRules } from '@features/booking/api/waitingList';
import { captureApiError } from '@core/sentry';
import type { ServiceType } from '@features/booking/state/types';

const MAX_DATES = 10;

const WaitingListCalendarPage = () => {
  const { t } = useTranslation('booking');
  const { goToNext } = useBookingFlow();
  const booking = useBooking();
  const restaurant = useRestaurant();
  const { dateLocale } = useLanguage();
  const { authState } = useAuth();

  // Filter calendar by the service selected in WL form (legacy behavior).
  // "Any service" (null) → fetch all service types.
  const allServiceTypes = (restaurant.serviceTypes || []) as ServiceType[];
  const wlServiceTypes = booking.wlServiceType
    ? [booking.wlServiceType]
    : allServiceTypes;
  const {
    mergedCalendar,
  } = useCalendarAvailability({
    restaurantId: restaurant.id,
    partySize: booking.diners,
    serviceTypes: wlServiceTypes,
    enabled: booking.diners > 0,
    meitreNetworkMenuId: booking.meitreNetworkMenuId,
    hasPreferredAccess: !!authState?.preferred,
  });

  // Build set of enabled dates (whitelist): only dates present in the calendar
  // with a clickable display type are selectable. All others are disabled.
  const enabledDateSet = useMemo(() => {
    const enabled = new Set<string>();
    for (const day of mergedCalendar) {
      if (isDisplayTypeClickable(day.displayType)) {
        enabled.add(day.date);
      }
    }
    return enabled;
  }, [mergedCalendar]);

  const isDateDisabled = useCallback((date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return !enabledDateSet.has(dateStr);
  }, [enabledDateSet]);

  const [selectedDates, setSelectedDates] = useState<Date[]>(
    booking.wlDates.map((d) => new Date(d + 'T12:00:00')),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleDate = (date: Date | undefined) => {
    if (!date) return;
    const dateStr = format(date, 'yyyy-MM-dd');

    setSelectedDates((prev) => {
      const exists = prev.some((d) => format(d, 'yyyy-MM-dd') === dateStr);
      if (exists) {
        return prev.filter((d) => format(d, 'yyyy-MM-dd') !== dateStr);
      }
      if (prev.length >= MAX_DATES) return prev;
      return [...prev, date];
    });
  };

  const removeDate = (dateStr: string) => {
    setSelectedDates((prev) =>
      prev.filter((d) => format(d, 'yyyy-MM-dd') !== dateStr),
    );
  };

  const handleContinue = async () => {
    if (selectedDates.length === 0) return;

    setIsLoading(true);
    setError(null);

    const dates = selectedDates.map((d) => format(d, 'yyyy-MM-dd'));
    booking.setWlDates(dates);

    try {
      // Legacy sends "-1" for "Any service" and "-1" for "Flexible" hour
      const serviceType = booking.wlServiceType ?? '-1';
      const hour = booking.wlPreferredTime ?? '-1';
      const alternatives = await fetchWaitingListAlternatives({
        dates,
        restId: restaurant.id,
        serviceType,
        pax: booking.diners,
        menus: booking.wlMenus,
      });

      if (alternatives && alternatives.length > 0) {
        // PATH A: alternatives found → show alternatives page.
        // flushSync so the orchestrator reads the fresh wlAlternatives
        // before goToNext() resolves the next active step.
        flushSync(() => {
          booking.updateBooking({ wlAlternatives: alternatives });
        });
        goToNext();
      } else {
        // PATH B: no alternatives → fetch guarantee rules and skip alternatives page
        const rules = await fetchWaitingListGuaranteeRules({
          dates,
          restId: restaurant.id,
          serviceType,
          pax: booking.diners,
          menus: booking.wlMenus,
          hour,
        });
        // Legacy always uses no_show for WL calendar (setGuaranteeInfoWlCalendar)
        const wlGuaranteeType = (rules.type === 'deposit' || rules.type === 'no_show')
          ? 'no_show' : 'traditional';
        // flushSync so the orchestrator sees guaranteeType + feeDeposit and
        // includes the GUARANTEE step before goToNext() picks the next path —
        // otherwise the user lands directly on /confirmation without a card token.
        flushSync(() => {
          booking.updateBooking({
            wlAlternatives: [],
            guaranteeType: wlGuaranteeType,
            feeDeposit: rules.fee,
            tipChargePercent: 0,
            feeGratuity: 0,
            feeGratuityPercent: 0,
          });
        });
        goToNext();
      }
    } catch (err) {
      captureApiError(err, { flow: 'waiting-list-calendar', restaurant: restaurant.id });
      setError(t('CONFIRMATION_ERROR'));
    } finally {
      setIsLoading(false);
    }
  };

  // Highlight selected dates on the calendar
  const modifiers = {
    selected: selectedDates,
  };

  return (
    <BookingPageLayout
      title={t('JOIN_THE_WAITING_LIST')}
      subtitle={t('WAITING_LIST_CALENDAR_SUBTITLE')}
    >
      <div className="w-full px-6 space-y-6 flex flex-col items-center">
        <Calendar
          mode="multiple"
          selected={selectedDates}
          onSelect={(dates) => {
            if (dates && dates.length <= MAX_DATES) {
              setSelectedDates(dates);
            }
          }}
          onDayClick={toggleDate}
          disabled={isDateDisabled}
          locale={dateLocale}
          numberOfMonths={1}
          modifiers={modifiers}
        />

        {selectedDates.length >= MAX_DATES && (
          <p className="text-sm text-amber-600 text-center">
            {t('WAITING_LIST_MAX_DATES_REACHED')}
          </p>
        )}

        {/* Selected dates pills */}
        {selectedDates.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm text-foreground/50">
              {t('WAITING_LIST_SELECTED_DATES', {
                count: selectedDates.length,
              })}
            </p>
            <div className="flex flex-wrap gap-2">
              {selectedDates
                .sort((a, b) => a.getTime() - b.getTime())
                .map((date) => {
                  const dateStr = format(date, 'yyyy-MM-dd');
                  return (
                    <span
                      key={dateStr}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-brand/10 text-brand text-sm"
                    >
                      {format(date, 'MMM d', { locale: dateLocale })}
                      <button
                        type="button"
                        onClick={() => removeDate(dateStr)}
                        className="hover:text-brand-dark"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  );
                })}
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 text-red-700 rounded-xl p-4 text-sm">
            {error}
          </div>
        )}
      </div>

      <div className="w-full px-6 mt-8">
        <Button
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md disabled:opacity-50"
          onClick={handleContinue}
          disabled={selectedDates.length === 0 || isLoading}
        >
          {isLoading
            ? t('CONFIRMATION_SUBMITTING')
            : t('WAITING_LIST_CONTINUE')}
        </Button>
      </div>
    </BookingPageLayout>
  );
};

export default WaitingListCalendarPage;
